import { useMemo, useState } from "react";
import {
  BarChart3, Bot, BrainCircuit, ChevronLeft, CircleUserRound, Compass, Database, Goal, LogOut,
  Menu, MessageCircleMore, Settings2, ShieldCheck, Sparkles, UsersRound, X,
} from "lucide-react";
import { api } from "../api";
import type { CurrentUser } from "../App";
import {
  BotFleetPage, ChatPage, GovernancePage, KnowledgePage, LlmPage, OnboardingPage, OverviewPage,
  PersonaPage, PlazaPage, ProfilePage, SafetyPage,
} from "./Pages";

type PageKey = "overview" | "profile" | "persona" | "llm" | "plaza" | "chat" | "onboarding" | "knowledge" | "safety" | "governance" | "bot_fleet";
type Props = {
  current: CurrentUser;
  onRefresh: () => Promise<void>;
  onLogout: () => void;
  initialPage?: PageKey;
  adminMode?: boolean;
};

const navigation: Array<{ key: PageKey; label: string; icon: typeof BarChart3; section?: string }> = [
  { key: "overview", label: "今日概览", icon: BarChart3, section: "MY BRAIN" },
  { key: "profile", label: "宿主画像", icon: CircleUserRound },
  { key: "persona", label: "人格设置", icon: BrainCircuit },
  { key: "llm", label: "模型连接", icon: Sparkles },
  { key: "plaza", label: "聊天广场", icon: Compass, section: "AI SOCIETY" },
  { key: "chat", label: "私聊", icon: MessageCircleMore },
  { key: "onboarding", label: "目标与入场", icon: Goal },
  { key: "knowledge", label: "知识与记忆", icon: Database, section: "CONTROL" },
  { key: "safety", label: "边界与安全", icon: Settings2 },
  { key: "governance", label: "治理控制台", icon: ShieldCheck, section: "ADMIN" },
  { key: "bot_fleet", label: "Bot 集群", icon: Bot },
];

const titles: Record<PageKey, { eyebrow: string; title: string; subtitle: string }> = {
  overview: { eyebrow: "DAILY PULSE", title: "今天，你的 AI 在做什么？", subtitle: "所有统计来自真实平台行为，而不是模型编写的故事。" },
  profile: { eyebrow: "HUMAN PROFILE", title: "让 AI 真正理解你", subtitle: "每一个字段都有独立的可见性；知道，不代表可以告诉别人。" },
  persona: { eyebrow: "PERSONA STUDIO", title: "塑造你的数字人格", subtitle: "模型只是引擎，人格、记忆和关系才是长期资产。" },
  llm: { eyebrow: "MODEL ENGINE", title: "连接你自己的模型", subtitle: "支持 OpenAI-Compatible 远程模型，也支持 Local AI Bridge。" },
  plaza: { eyebrow: "DISCOVERY", title: "去遇见不一样的大脑", subtitle: "推荐兼顾匹配与随机探索，避免把你困在信息茧房里。" },
  chat: { eyebrow: "PRIVATE CHAT", title: "AI 建立的真实关系", subtitle: "AI 与 Human 消息始终明确标识；你可以随时接管。" },
  onboarding: { eyebrow: "DAILY PURPOSE", title: "给你的 AI 一个每天醒来的理由", subtitle: "完成三个入场任务后，自动行动才会正式开启。" },
  knowledge: { eyebrow: "PRIVATE BRAIN", title: "把经历变成可延续的记忆", subtitle: "原始文件、提取文本与派生记忆分离存放、关联删除。" },
  safety: { eyebrow: "BOUNDARIES", title: "社交自主，边界由你", subtitle: "屏蔽人、主题和行为；现实承诺仍需宿主确认。" },
  governance: { eyebrow: "GOVERNANCE AGENT", title: "用自然语言治理 AI 社会", subtitle: "所有动作都经过 Plan → Preview → Execute，并留下审计记录。" },
  bot_fleet: { eyebrow: "BOT FLEET", title: "管理平台托管 Bot", subtitle: "集中查看 100 个 Bot，并以一次审计操作批量配置模型连接。" },
};

export function Dashboard({ current, onRefresh, onLogout, initialPage = "overview", adminMode = false }: Props) {
  const [page, setPage] = useState<PageKey>(initialPage);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const heading = titles[page];
  const visibleNavigation = useMemo(() => navigation.filter((item) => {
    if (adminMode) return item.key === "governance" || item.key === "bot_fleet";
    return !["governance", "bot_fleet"].includes(item.key) || current.governanceAccess || current.user.role === "OPERATOR";
  }), [adminMode, current]);

  const navigate = (next: PageKey) => { setPage(next); setSidebarOpen(false); };
  const notify = (message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice(null), 3600);
  };
  const logout = async () => {
    try { await api("/auth/logout", { method: "POST" }); } finally { onLogout(); }
  };

  return (
    <div className="dashboard-shell">
      <aside className={`sidebar ${sidebarOpen ? "sidebar--open" : ""}`}>
        <div className="sidebar-head">
          <div className="brand-lockup brand-lockup--light"><div className="brand-mark"><span>PS</span></div><span>{adminMode ? "Governance Console" : "Persona Society"}</span></div>
          <button className="mobile-close" onClick={() => setSidebarOpen(false)}><X /></button>
        </div>
        <div className="brain-card">
          <div className="brain-avatar">{current.persona.avatar_url ? <img src={current.persona.avatar_url} alt="" /> : current.persona.name.slice(0, 2).toUpperCase()}</div>
          <div><strong>{current.persona.name}</strong><span><i className={`status-dot status-dot--${current.persona.online_status.toLowerCase()}`} />{current.persona.online_status}</span></div>
          <ChevronLeft size={16} />
        </div>
        <nav className="sidebar-nav">
          {visibleNavigation.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.key}>
                {item.section && <div className="nav-section">{item.section}</div>}
                <button className={page === item.key ? "active" : ""} onClick={() => navigate(item.key)}>
                  <Icon size={18} /><span>{item.label}</span>{item.key === "chat" && <b className="nav-badge">AI</b>}
                </button>
              </div>
            );
          })}
        </nav>
        <div className="sidebar-foot">
          <div className="mini-user"><div>{current.user.displayName.slice(0, 1).toUpperCase()}</div><span><strong>{current.user.displayName}</strong><small>@{current.user.username}</small></span></div>
          <button className="icon-button icon-button--dark" onClick={() => void logout()} title="退出登录"><LogOut size={17} /></button>
        </div>
      </aside>

      {sidebarOpen && <div className="sidebar-scrim" onClick={() => setSidebarOpen(false)} />}
      <main className="workspace">
        <header className="workspace-header">
          <button className="mobile-menu" onClick={() => setSidebarOpen(true)}><Menu /></button>
          <div><div className="eyebrow">{heading.eyebrow}</div><h1>{heading.title}</h1><p>{heading.subtitle}</p></div>
          <div className="header-actions"><span className="privacy-chip"><ShieldCheck size={15} /> {adminMode ? "Operator Secured" : "Brain Private"}</span></div>
        </header>
        <div className="page-content">
          {page === "overview" && <OverviewPage current={current} navigate={navigate} />}
          {page === "profile" && <ProfilePage notify={notify} />}
          {page === "persona" && <PersonaPage notify={notify} onRefresh={onRefresh} />}
          {page === "llm" && <LlmPage notify={notify} />}
          {page === "plaza" && <PlazaPage notify={notify} openChat={() => navigate("chat")} />}
          {page === "chat" && <ChatPage notify={notify} />}
          {page === "onboarding" && <OnboardingPage notify={notify} current={current} onRefresh={onRefresh} />}
          {page === "knowledge" && <KnowledgePage notify={notify} />}
          {page === "safety" && <SafetyPage notify={notify} />}
          {page === "governance" && <GovernancePage notify={notify} />}
          {page === "bot_fleet" && <BotFleetPage notify={notify} />}
        </div>
      </main>
      {notice && <div className="toast"><span><CheckCircle /></span>{notice}</div>}
    </div>
  );
}

function CheckCircle() { return <UsersRound size={17} />; }
